import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  afterRender,
  computed,
  effect,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { LiveClient, SOCKET_STATES, createClient } from '@deepgram/sdk';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { DeviceDetectorService } from 'ngx-device-detector';
import { posthog } from 'posthog-js';
import { Subject, Subscription, debounceTime, interval, take } from 'rxjs';
import { SuccessResponse } from '../../../../../backend/src/core/request-response';
import { PingSessionErrCodes } from '../../../../../backend/src/core/session/session.gate';
import { QuestionButtonComponent } from '../../basic/buttons/question-button/question-button.component';
import { ImageOpenInFullComponent } from '../../basic/image/image-open-in-full/image-open-in-full.component';
import { TextImageModalComponent } from '../../basic/modal/text-image-modal/text-image-modal.component';
import { MarkdownItPipe } from '../../basic/pipes/markdown-it.pipe';
import { DeepgramService } from '../../core/deepgram/deepgram.service';
import { OpenaiModel } from '../../core/openai/openai';
import { OpenaiService } from '../../core/openai/openai.service';
import { PostHogEvent } from '../../core/posthog/posthog.event';
import { getCheckEndMeetingFunctions, getCheckEndMeetingPrompt } from '../../core/prompt/check-end-meeting.prompt';
import { getCustomerPrompt } from '../../core/prompt/customer.prompt';
import { ResumeBackend } from '../../core/resume/resume';
import { SessionMessage, SessionMessageSubData } from '../../core/session-message/session-message';
import { SessionMessageService } from '../../core/session-message/session-message.service';

import { toSignal } from '@angular/core/rxjs-interop';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { environment } from '../../../environments/environment';
import { ObjectionType } from '../../core/objection/objection';
import { ObjectionService } from '../../core/objection/objection.service';
import { convertResumeJsonToMarkdown } from '../../core/prompt/resume/get-resume-content.prompt';
import { getOneConversationPrompt } from '../../core/prompt/shared/shared.prompt';
import { ScreenRecordingService } from '../../core/screen-recording/screen-recording.service';
import {
  FREE_TRIAL_MINS,
  SESSION_LENGTH,
  Session,
  SessionAnswerMode,
  SessionCreativity,
  SessionPerformance,
  SessionType,
  canFreeTrial,
  isCreditsEnoughForSessionType,
} from '../../core/session/session';
import { SessionService } from '../../core/session/session.service';
import { UserClerkService } from '../../core/user/user-clerk.service';
import { initSalesCycleData } from '../../core/workspace/init-workspace.data';
import { SalesCycle } from '../../core/workspace/workspace';
import { WorkspaceHolderService } from '../../core/workspace/workspace.holder.service';
import { ProfileCardSmallComponent } from '../../domain/profile-card/profile-card-small/profile-card-small.component';
import { SessionSurveyComponent } from '../../domain/session-copilot/session-survey/session-survey.component';
import { EnumUtils } from '../../shared/utils/enum-utils';
import { MainSidebarComponent } from '../_shared/main-sidebar/main-sidebar.component';

@Component({
  selector: 'app-session-copilot-run',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzButtonModule,
    NzListModule,
    NzSelectModule,
    NzCommentModule,
    NzAvatarModule,
    NzCheckboxModule,
    NzModalModule,
    NzAlertModule,
    NzSegmentedModule,
    NzTagModule,
    NzIconModule,
    NzTabsModule,
    NzSwitchModule,
    NzStepsModule,
    NzMessageModule,
    NzImageModule,
    NzPopconfirmModule,
    NzInputModule,
    NzBadgeModule,

    MatTooltipModule,

    MarkdownItPipe,

    MainSidebarComponent,
    ImageOpenInFullComponent,
    QuestionButtonComponent,
    SessionSurveyComponent,
    TextImageModalComponent,
    ProfileCardSmallComponent,
  ],
  templateUrl: './session-copilot-run.component.html',
  styleUrl: './session-copilot-run.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition(':leave', [animate('300ms', style({ opacity: 0 }))]),
    ]),
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', width: '0px' }),
        animate('300ms ease-out', style({ transform: 'translateX(0%)', width: '500px' })),
      ]),
      transition(':leave', [animate('300ms ease-in', style({ transform: 'translateX(100%)', width: '0px' }))]),
    ]),
  ],
})
export class SessionCopilotRunComponent {
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

  tempAiText = '';

  @ViewChild('meetingBody') meetingBody!: ElementRef<HTMLElement>;

  showChatMessage = signal<boolean>(false);

  private readonly RECONNECT_INTERVAL = 1000;

  aiTalkingStartTime?: DateTime;
  aiTalkingEndTime?: DateTime;

  myAnswerStartTime?: DateTime;
  myAnswerEndTime?: DateTime;

  sessionMessageList: SessionMessage[] = [];

  sales = computed(() => {
    const portrait = this.userClerkService.portraitUrl();
    const name = this.userClerkService.getUser()?.firstName;

    return {
      portrait,
      name,
    };
  });

  stopWorking = false;

  everySecond$ = interval(1000); // Create an observable that emits a value every second
  everySecondSubscription?: Subscription;

  everyTwoMinutes$ = interval(60000 * 2);
  everyTwoMinutesSubscription?: Subscription;

  freeTrialWarning = signal<string>('You are in the free trial. 10 minutes remaining.');
  freeTrialMinsLeft = signal<number>(10);

  sessionWarning = signal<string>('');
  sessionMinsLeft = signal<number>(SESSION_LENGTH);

  showSessionEndModal = signal<boolean>(false);

  // session id
  @Input() id?: string;
  session = signal<Session | undefined>(undefined);

  codeTabIndex = 0;

  configStepIndex = signal<number>(0);

  ConnectionStatus = ConnectionStatus;
  SessionType = SessionType;
  SessionPerformance = SessionPerformance;

  isAutoScroll = true;
  isPause = signal<boolean>(false);

  isTabOn = signal<boolean>(false);
  isMicOn = signal<boolean>(false);
  isStoppedSharingTab = signal<boolean>(false);

  isUserSpeaking = signal<boolean>(false);
  isAiSpeaking = signal<boolean>(false);
  isAiThinking = signal<boolean>(false);

  humanStartSpeaking = signal<boolean>(false);

  allObjectionList = toSignal(this.objectionService.itemList$(), { initialValue: [] });

  getResumeText(resume?: ResumeBackend) {
    if (!resume) {
      return '';
    }
    return `${resume.subData.content?.firstName} - ${resume.subData.content?.mostRecentPosition} @
    ${resume.subData.content?.mostRecentCompany}`;
  }

  customer = computed(() => {
    const resume = this.session()?.subData?.resume;
    return {
      portrait: resume?.subData.content?.portraitImageUrl ?? '/assets/images/ai-profile/sofia_v1.png',
      name: resume?.subData.content?.firstName ?? 'Sofia',
    };
  });

  minsUsed = signal<number>(0);

  myAnswerDiff = signal<string>('');

  isConfig = true;

  isFreeTrialSession = false;

  microphoneList = signal<MediaDeviceInfo[]>([]);

  selectedMicrophone?: MediaDeviceInfo;

  tabAudioTracks?: MediaStreamTrack[];

  private myAnswerDebouncedSubscription?: Subscription;

  micSocket?: LiveClient;

  deepgramKey?: string;

  pingSessionCount = 0;

  analyzingImage = signal<boolean>(false);

  has0FreeTrialWarning = false;

  has15Warning = false;
  has5Warning = false;
  has1Warning = false;
  has0Warning = false;

  constructor(
    private notification: NzNotificationService,
    private modalService: NzModalService,
    private deepgramService: DeepgramService,
    private msg: NzMessageService,
    private ref: ChangeDetectorRef,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private openaiService: OpenaiService,
    private userClerkService: UserClerkService,
    private sessionService: SessionService,
    private sessionMessageService: SessionMessageService,
    private workspaceHolderService: WorkspaceHolderService,
    private screenRecordingService: ScreenRecordingService,
    private objectionService: ObjectionService
  ) {
    effect(() => {
      const isStartedVideo = this.isStartedVideo();

      if (isStartedVideo) {
        this.startCamera();
      } else {
        this.stopCamera();
      }
    });

    this.everySecondSubscription = this.everySecond$.subscribe(() => {
      if (this.isFreeTrialSession) {
        const dateStr = this.userClerkService.publicMetadata.freeTrialDateStr;
        const date = DateTime.fromISO(dateStr!);

        this.minsUsed.set(Math.floor(-date.diffNow('minutes').minutes));

        const diff = Math.ceil(FREE_TRIAL_MINS + date.diffNow('minutes').minutes);

        this.freeTrialWarning.set(
          `Your free trial will end in ${diff} minutes. You can purchase points to unlock full 60-minute practice with complete feature access.`
        );

        this.freeTrialMinsLeft.set(diff);

        if (diff <= 0) {
          if (!this.has0FreeTrialWarning) {
            this.pauseSession();

            posthog.capture(PostHogEvent.sessionFreeTrialExpired, {
              session: this.session!,
            });

            this.sessionService.showNotEnoughPointsModal.set(true);
            this.router.navigate(['/simulated-meetings']);
          } else {
            this.has0FreeTrialWarning = true;
          }
        }
      } else {
        const startDate = this.session()!.startTime;

        if (!startDate) {
          posthog.capture(PostHogEvent.sessionNoStartTime, {
            session: this.session!,
          });
          return;
        }

        this.minsUsed.set(Math.floor(-startDate.diffNow('minutes').minutes));

        const diff = Math.ceil(SESSION_LENGTH + startDate.diffNow('minutes').minutes);
        this.sessionMinsLeft.set(diff);

        if (diff === 15) {
          if (!this.has15Warning) {
            this.msg.warning(
              'The current practice will be terminated in 15 minutes. Please manage your time properly. You can start a new practice if needed.'
            );
            this.has15Warning = true;
          }
        } else if (diff === 5) {
          if (!this.has5Warning) {
            this.msg.warning(
              'The current practice will be terminated in 5 minutes. Please manage your time properly. You can start a new practice if needed.'
            );
            this.has5Warning = true;
          }
        } else if (diff === 1) {
          if (!this.has1Warning) {
            this.msg.warning(
              'The current practice will be terminated in 1 minutes. Please manage your time properly. You can start a new practice if needed.'
            );
            this.has1Warning = true;
          }
        } else if (diff === 0) {
          if (!this.has0Warning) {
            this.pauseSession();
            this.onEndSessionClick();
          } else {
            this.has0Warning = true;
          }
        }
      }
    });

    this.everyTwoMinutesSubscription = this.everyTwoMinutes$.subscribe(() => {
      this.sessionService
        .pingSession$(this.session()!)
        .pipe(take(1))
        .subscribe({
          next: (data: SuccessResponse) => {
            if (!data.isSuccess) {
              this.pauseSession();

              posthog.capture(PostHogEvent.sessionPingError, {
                response: data,
                session: this.session,
              });

              if (data.errCode === PingSessionErrCodes.NOT_FOUND) {
                this.msg.error(`Cannot find the practice with id: ${this.id}. Return to the practice list page.`);
              } else if (data.errCode === PingSessionErrCodes.NO_CREDITS) {
                this.msg.error(
                  "You don't have enough points to launch the practice. Return to the practice list page."
                );
                this.sessionService.showNotEnoughPointsModal.set(true);
              } else if (data.errCode === PingSessionErrCodes.NO_STATUS_TIME_STR) {
                this.msg.error('Cannot find the status time. Return to the practice list page.');
              } else if (data.errCode === PingSessionErrCodes.STATUS_NOT_ONGOING) {
                this.msg.error(`The practice status is ${this.session()!.status}. Return to the practice list page.`);
              } else {
                this.msg.error('Unknown error. Return to the practice list page.');
              }
              this.router.navigate(['/simulated-meetings']);
            }
          },
          error: (err) => {
            posthog.capture(PostHogEvent.sessionPingException, {
              error: err,
              session: this.session,
            });

            this.pingSessionCount++;

            if (this.pingSessionCount >= 3) {
              this.pauseSession();
              this.msg.error('Cannot reach to the server. Return to the practice list page.');
              this.router.navigate(['/simulated-meetings']);
            }
          },
        });
    });

    afterRender({
      read: () => {
        if (this._shouldScrollToBottom) {
          this.scrollAllToBottom();
          this._shouldScrollToBottom = false;
        }
      },
    });

    effect(() => {
      this.myAnswerList();
    });

    effect(
      () => {
        const microphoneList = this.microphoneList();

        if (microphoneList.length > 0) {
          this.configStepIndex.set(2);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const isMicOn = this.isMicOn();

        if (isMicOn) {
          this.isPause.set(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      if (
        (!this.isPause() && this.micConnectStatus() === ConnectionStatus.closed) ||
        this.micConnectStatus() === ConnectionStatus.error
      ) {
        this.notification.error('Mic Error Detected', this.micConnectStatusReason());
      } else if (!this.isPause() && this.micConnectStatus() === ConnectionStatus.warning) {
        this.notification.warning('Mic Warning Detected', this.micConnectStatusReason());
      }
    });

    this.myAnswerDebouncedSubscription = this.myAnswerDebounced.pipe(debounceTime(1000)).subscribe(() => {
      console.log('myAnswerDebounced:', this.tempSpeechFinal);
      if (this.tempSpeechFinal) {
        // this.talkToLlm();
      }
    });

    this.myAnswerDebouncedSubscription = this.myAnswerDebounced.pipe(debounceTime(5000)).subscribe(() => {
      this.tempSpeechFinal = true;
      // this.talkToLlm();
    });
  }

  cameraMediaStream: MediaStream | undefined;

  startCamera(): void {
    const video = document.getElementById('userCamera') as HTMLVideoElement;

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const videoConstraints: MediaTrackConstraints | boolean = this.selectedCamera
        ? { deviceId: { exact: this.selectedCamera.deviceId } }
        : true;

      navigator.mediaDevices
        .getUserMedia({ video: videoConstraints })
        .then((stream) => {
          this.cameraMediaStream = stream;
          video.srcObject = stream;
          video.play();
        })
        .catch((err) => {
          console.error('Error accessing the camera: ', err);
        });
    } else {
      console.error('getUserMedia not supported on your browser!');
    }
  }

  stopCamera(): void {
    if (this.cameraMediaStream) {
      this.cameraMediaStream.getTracks().forEach((track) => track.stop());
      this.cameraMediaStream = undefined;
    }
  }

  isStartedVideo = signal<boolean>(false);

  async onStartVideoClick() {
    this.isStartedVideo.set(!this.isStartedVideo());

    // TODO: Start camera
  }

  isMuted = signal<boolean>(false);

  async onMuteClick() {
    this.isMuted.set(!this.isMuted());

    const isMuted = this.isMuted();

    if (isMuted) {
      this.pauseSession();
    } else {
      await this.getMic();
    }
  }

  onChatClick() {
    this.showChatMessage.set(!this.showChatMessage());
  }

  async onPauseChange(isPaused: boolean) {
    if (isPaused) {
      posthog.capture(PostHogEvent.sessionPausedByHuman, {
        session: this.session!,
      });

      this.pauseSession();
    } else {
      await this.getMic();
    }
  }

  private pauseSession() {
    this.isPause.set(true);
    if (this.micSocket?.getReadyState() === SOCKET_STATES.open) {
      this.micSocket.requestClose();
    }
  }

  onRefreshPageClick() {
    window.location.reload();
  }

  refreshPage() {
    location.reload();
  }

  selectedPerformanceIndex?: number;
  performanceOptions = EnumUtils.getAllValues(SessionPerformance);

  selectedCreativityIndex?: number;
  creativityOptions = EnumUtils.getAllValues(SessionCreativity);

  selectedAnswerModeIndex?: number;
  answerModeOptions = EnumUtils.getAllValues(SessionAnswerMode);

  ngOnInit() {
    if (!this.id) {
      this.router.navigate(['/']);
    }

    if (this.deviceService.browser !== 'Chrome' && this.deviceService.browser !== 'MS-Edge-Chromium') {
      this.notification.error('Not Support', 'Please use Chrome browser to use this feature.', { nzDuration: 10000 });
      this.router.navigate(['/']);
    }

    if (!this.deviceService.isDesktop()) {
      this.notification.error('Not Support', 'Please use a computer to use this feature.', { nzDuration: 10000 });
      this.router.navigate(['/']);
    }

    this.getCameras().then((devices) => {
      this.cameraList.set(devices);
    });

    this.sessionService
      .getSessionById$(this.id!)
      .pipe(take(1))
      .subscribe((data) => {
        this.session.set(data);

        if (!this.session) {
          this.msg.error('The meeting couldn’t be found. Please create one first.');
          this.router.navigate(['/']);
        }

        if (!isCreditsEnoughForSessionType(this.session()!.type, this.userClerkService.credits())) {
          if (
            this.session()!.id === this.userClerkService.publicMetadata.freeTrialSessionId ||
            canFreeTrial(this.userClerkService.publicMetadata)
          ) {
            // Free trial session
            this.isFreeTrialSession = true;
          } else {
            this.msg.error("You don't have enough points to launch the meeting. Return to the meeting list page.");
            this.sessionService.showNotEnoughPointsModal.set(true);
            this.router.navigate(['/simulated-meetings']);
          }
        }

        if (this.sessionService.isOngoing(this.session())) {
          this.selectedPerformanceIndex = EnumUtils.getEnumIndex(
            this.session()!.subData.performance,
            SessionPerformance
          );
          this.selectedCreativityIndex = EnumUtils.getEnumIndex(this.session()!.subData.creativity, SessionCreativity);
        } else {
          this.msg.error('This practice has finished. Return to the practice list page.');
          this.router.navigate(['/simulated-meetings']);
        }
      });

    this.getMicrophones().then((devices) => {
      this.microphoneList.set(devices);
    });
  }

  async ngOnDestroy() {
    this.stopCamera();

    if (this.volumeCheckInterval) {
      clearInterval(this.volumeCheckInterval);
    }

    this.pauseSession();

    this.sessionService.stopMicSharing();

    this.everySecondSubscription?.unsubscribe();
    this.myAnswerDebouncedSubscription?.unsubscribe();
    this.everyTwoMinutesSubscription?.unsubscribe();
  }

  selectedCamera?: MediaDeviceInfo;
  cameraList = signal<MediaDeviceInfo[]>([]);

  onCameraSelect(camera: MediaDeviceInfo) {
    this.selectedCamera = camera;
  }

  async getCameras(): Promise<MediaDeviceInfo[]> {
    try {
      // Request permissions for media devices
      await navigator.mediaDevices.getUserMedia({ video: true });

      // Enumerate devices to get the list of devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter out the devices to get only the video inputs (cameras)
      const cameras = devices.filter((device) => device.kind === 'videoinput');

      this.cameraList.set(cameras);

      if (cameras.length > 0) {
        this.selectedCamera = cameras[0];
      }
      return cameras;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      return [];
    }
  }

  async getMicrophones(): Promise<MediaDeviceInfo[]> {
    try {
      // Request permissions for media devices
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Enumerate devices to get the list of devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter out the devices to get only the microphones
      const microphones = devices.filter((device) => device.kind === 'audioinput');

      if (microphones.length > 0) {
        this.selectedMicrophone = microphones[0];
      }
      return microphones;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      return [];
    }
  }

  onGobackClick() {
    this.router.navigate(['simulated-meetings']);
  }

  isStartButtonDisabled = computed(() => {
    if (this.configStepIndex() < 6) {
      return true;
    }
    return false;
  });

  isEndSurveyVisible = false;

  onEndSessionClick() {
    if (this.session()) {
      this.audioPlayer.nativeElement.pause();

      this.everyTwoMinutesSubscription?.unsubscribe();
      this.pauseSession();

      const fileUrl = this.sessionService.getRecordingFileUrl(this.session()!);
      this.screenRecordingService.stopRecordingAndSaveToWebm(fileUrl);

      this.sessionService
        .endSession$(this.session()!, `${environment.filesDomain}/${fileUrl}`)
        .pipe(take(1))
        .subscribe(() => {
          posthog.capture(PostHogEvent.sessionManuallyEnd);
        });

      this.isEndSurveyVisible = true;
    }
  }

  isNeedToShareAudioModalVisible = false;

  async onStartClick() {
    this.isConfig = false;

    await this.getMic();

    try {
      await this.screenRecordingService.startRecording(this.sessionService.micStream!);
    } catch (error) {
      this.msg.error(
        'Failed to start recording. Please restart Chrome and try again. If the problem persists, contact us at support@peakmojo.ai',
        { nzDuration: 10000 }
      );
    }

    const initMessage = 'Hello, who is this?';

    this.tempAiText = initMessage;

    this.showChatMessage.set(true);

    this.deepgramService
      .textToSpeech$(this.session()!.id, initMessage, {
        voice: this.session()!.subData.resume?.subData?.content?.voice,
      })
      .pipe(take(1))
      .subscribe((url) => {
        this.currentAudioUrl = url;
        this.audioPlayer.nativeElement.src = url;
        this.aiTalkingStartTime = DateTime.local();
        this.playAudio();
        this.isAiThinking.set(false);
        this.isAiSpeaking.set(true);
      });
  }

  //#region Grant Permissions
  async onGrantPermissionClick() {
    await this.screenRecordingService.grantPermission();
    this.configStepIndex.set(6);
  }
  //#endregion

  //#region Share Tab
  async onShareBrowserTabClick() {
    try {
      // Define constraints for the screen capture
      const constraints: DisplayMediaStreamOptions = {
        video: true,
        audio: true,
      };

      // Prompt the user to select a screen, window, or tab to share
      const media = await navigator.mediaDevices.getDisplayMedia(constraints);

      this.sessionService.tabMedia = media;

      if (!media || media?.getVideoTracks().length === 0) {
        this.modalService.error({
          nzTitle: 'Please share a tab.',
          nzContent: 'Please share your interview tab.',
        });
        this.sessionService.stopTabSharing();
        return;
      }

      const track = media.getVideoTracks()[0];
      const settings = track.getSettings();

      if (settings.displaySurface !== 'browser') {
        this.modalService.error({
          nzTitle: 'Please share a browser tab',
          nzContent: 'Please share the specific browser tab you have opened with your interviewer.',
        });

        this.sessionService.stopTabSharing();
        return;
      }

      if (media.getAudioTracks().length === 0) {
        this.modalService.error({
          nzTitle: 'Please share your tab with audio.',
          nzContent: 'Please make sure you enabled the audio.',
        });
        return;
      }

      this.isStoppedSharingTab.set(false);

      track.addEventListener('ended', () => {
        this.isStoppedSharingTab.set(true);
      });

      this.tabAudioTracks = media?.getAudioTracks();

      this.configStepIndex.set(3);

      this.ref.detectChanges();
    } catch (error) {
      console.error('Error sharing the screen:', error);
    }
  }
  //#endregion

  // === START ===
  myAnswerList = signal<string[]>([]);
  aiResponseList = signal<string[]>([]);

  messageList = computed(() => {
    const aiResponseList = this.aiResponseList();
    const myAnswerList = this.myAnswerList();

    const result: string[] = [];

    const maxLength = Math.max(myAnswerList.length, aiResponseList.length);

    for (let i = 0; i < maxLength; i++) {
      if (i < aiResponseList.length) {
        result.push(aiResponseList[i]);
      }
      if (i < myAnswerList.length) {
        result.push(myAnswerList[i]);
      }
    }

    return result;
  });

  micConnectStatus = signal<ConnectionStatus>(ConnectionStatus.loading);
  micConnectStatusReason = signal<string>('');

  private myAnswerDebounced = new Subject<void>();
  private tempSpeechFinal = false;

  selectedIndex = -1;

  onSessionerMessageMouseover(i: number) {
    this.selectedIndex = i;
  }

  onAiResponseMessageMouseover(i: number) {
    this.selectedIndex = i;
  }

  onMyAnswerMessageMouseover(i: number) {
    this.selectedIndex = i;
  }

  handleNeedToShareAudioModalOk() {
    location.reload();
  }

  private videoElement?: HTMLVideoElement;

  private currentAudioUrl?: string;

  addAiTalking(lastMyAnswerContent: string, newContent: string) {
    if (lastMyAnswerContent) {
      // Combine
      const previousMessage = this.sessionMessageList[this.sessionMessageList.length - 1];
      const sessionMessageSubData: SessionMessageSubData = {
        salesMessage: `${lastMyAnswerContent} ${newContent}`,
        salesStartTimeStr: previousMessage.subData.salesStartTimeStr,
        salesEndTimeStr: this.myAnswerEndTime!.toUTC().toISO()!,

        salesSeconds: this.myAnswerEndTime!.diff(DateTime.fromISO(previousMessage.subData.salesStartTimeStr), 'seconds')
          .seconds,

        customerMessage: this.tempAiText,
        customerAudioUrl: this.currentAudioUrl,
        customerStartTimeStr: this.aiTalkingStartTime!.toUTC().toISO()!,
        customerEndTimeStr: this.aiTalkingEndTime!.toUTC().toISO()!,

        customerSeconds: this.aiTalkingEndTime!.diff(this.aiTalkingStartTime!, 'seconds').seconds,
      };

      previousMessage.subData = sessionMessageSubData;

      this.sessionMessageService
        .updateSessionMessage$(previousMessage)
        .pipe(take(1))
        .subscribe((updatedItem) => {
          this.sessionMessageList[this.sessionMessageList.length - 1] = updatedItem as SessionMessage;

          this.sessionMessageService
            .findObjection$(this.session()?.subData.template as any, updatedItem as SessionMessage)
            .pipe(take(1))
            .subscribe((sessionMessage) => {
              if (sessionMessage.subData.objectionId) {
                const objection = this.allObjectionList().find(
                  (objection) => objection.id === sessionMessage.subData.objectionId
                );

                if (objection?.type === ObjectionType.emotional) {
                  this.sessionMessageService
                    .evaluateSoftSkillsObjectionAnswer$(
                      updatedItem as SessionMessage,
                      this.sessionMessageList,
                      objection!
                    )
                    .pipe(take(1))
                    .subscribe();
                }
              }
            });
        });
    } else {
      const sessionMessageSubData: SessionMessageSubData = {
        salesMessage: newContent,
        salesStartTimeStr: this.myAnswerStartTime!.toUTC().toISO()!,
        salesEndTimeStr: this.myAnswerEndTime!.toUTC().toISO()!,

        salesSeconds: this.myAnswerEndTime!.diff(this.myAnswerStartTime!, 'seconds').seconds,

        customerMessage: this.tempAiText,
        customerAudioUrl: this.currentAudioUrl,
        customerStartTimeStr: this.aiTalkingStartTime!.toUTC().toISO()!,
        customerEndTimeStr: this.aiTalkingEndTime!.toUTC().toISO()!,

        customerSeconds: this.aiTalkingEndTime!.diff(this.aiTalkingStartTime!, 'seconds').seconds,
      };

      this.sessionMessageService
        .addSessionMessage$(this.session()!, sessionMessageSubData)
        .pipe(take(1))
        .subscribe((data) => {
          this.sessionMessageList.push(data);

          this.sessionMessageService
            .findObjection$(this.session()?.subData.template as any, data)
            .pipe(take(1))
            .subscribe((data) => {
              if (!data.subData.objectionId) {
                this.openaiService
                  .getAnswer$({
                    messageList: [{ role: 'user', content: getCheckEndMeetingPrompt(this.tempAiText) }],
                    config: { openaiModel: OpenaiModel.gpt4, functions: getCheckEndMeetingFunctions() },
                  })
                  .pipe(take(1))
                  .subscribe((isEndResult) => {
                    if (isEndResult.isEnding) {
                      this.pauseSession();
                      this.msg.info(`The meeting has ended because: ${isEndResult.explanation}`, { nzDuration: 15000 });
                      this.onEndSessionClick();
                    }
                  });
              } else {
                const objection = this.allObjectionList().find(
                  (objection) => objection.id === data.subData.objectionId
                );

                if (objection?.type === ObjectionType.emotional) {
                  this.sessionMessageService
                    .evaluateSoftSkillsObjectionAnswer$(data as SessionMessage, this.sessionMessageList, objection!)
                    .pipe(take(1))
                    .subscribe();
                }
              }
            });
        });
    }
  }

  ngAfterViewInit(): void {
    this.videoElement = document.createElement('video');
    this.videoElement.style.display = 'none'; // Hide the video element
    document.body.appendChild(this.videoElement); // Append to body to ensure playback

    this.audioPlayer.nativeElement.addEventListener('ended', () => {
      this.aiTalkingEndTime = DateTime.utc();
      this.isAiSpeaking.set(false);
      this.humanStartSpeaking.set(false);

      this.addToAiResponse();
    });
  }

  addToAiResponse() {
    this.myAnswerList.update((data) => {
      return [...data, ''];
    });

    this.aiResponseList.update((data) => {
      if (this.tempAiText) {
        if (data.length === 0) {
          data = [this.tempAiText, ''];
        } else {
          data[data.length - 1] = this.tempAiText;
          data.push('');
        }
        if (this.isAutoScroll) {
          this._shouldScrollToBottom = true;
        }
      }
      return [...data];
    });
  }

  isDismiss = false;
  onDismissClick() {
    this.isDismiss = true;
  }

  async onMicReconnectClick(notification: any) {
    await this.getMic();
    notification.close();
  }

  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private dataArray?: Uint8Array;

  chosenDeviceId?: string;

  async getMic() {
    if (this.selectedMicrophone) {
      const that = this;
      this.chosenDeviceId = this.selectedMicrophone!.deviceId;

      this.micSocket?.removeAllListeners();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
          deviceId: { exact: this.chosenDeviceId },
        },
      });

      this.audioContext = new AudioContext();
      let source: MediaStreamAudioSourceNode;

      try {
        source = this.audioContext.createMediaStreamSource(stream);
      } catch (error) {
        this.msg.error(
          'Your microphone seems to be having issues. Please restart Chrome and try again. If the problem persists, contact us at support@peakmojo.ai ',
          { nzDuration: 10000 }
        );
        return;
      }

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      this.sessionService.micStream = stream;

      const mediaRecorder = new MediaRecorder(stream);

      this.deepgramService
        .getTempApiKeyForSession$(this.session()!.id!)
        .pipe(take(1))
        .subscribe((keyResponse) => {
          const key = keyResponse.key;
          const _deepgram = createClient(key);

          that.micSocket = _deepgram.listen.live({
            model: 'nova-2',
            smart_format: true,
            interim_results: true,
            utterance_end_ms: 1000,
            filler_words: true,
            endpointing: 300,
            no_delay: true,
          });

          const socket = that.micSocket;

          socket.on('open', async () => {
            this.micConnectStatusReason.set('');
            this.micConnectStatus.set(ConnectionStatus.connected);
            this.isMicOn.set(true);

            socket.on('UtteranceEnd', (data: any) => {
              console.log('UtteranceEnd');
              this.talkToLlm();
            });

            socket.on('Results', (data: any) => {
              const transcript = data.channel.alternatives[0].transcript;

              if (transcript) {
                this.myAnswerDebounced.next();

                if (!this.humanStartSpeaking()) {
                  this.myAnswerStartTime = DateTime.utc();
                  this.humanStartSpeaking.set(true);
                }

                // Stop speaking
                if (!this.audioPlayer.nativeElement.paused) {
                  if (this.audioPlayer.nativeElement.currentTime < 3) {
                  } else {
                    this.humanStartSpeaking.set(false);
                    this.addToAiResponse();
                  }

                  this.stopAudio();

                  console.log('Stop audio');
                }

                if (data.is_final) {
                  this.myAnswerList.update((myAnswer) => {
                    if (myAnswer.length === 0) {
                      this.tempSpeechFinal = data.speech_final;
                      return [transcript];
                    } else {
                      const lastContent = myAnswer[myAnswer.length - 1];

                      if (lastContent) {
                        // Combined
                      } else {
                        // New
                      }

                      this.myAnswerEndTime = DateTime.utc();
                      this.addAiTalking(lastContent, transcript);

                      myAnswer[myAnswer.length - 1] = `${lastContent} ${transcript}`;

                      this.tempSpeechFinal = data.speech_final;
                      return [...myAnswer];
                    }
                  });
                }

                if (this.isAutoScroll) {
                  this._shouldScrollToBottom = true;
                }
              }
            });

            socket.on('error', (e: any) => {
              posthog.capture(PostHogEvent.sessionMicError, {
                error: e,
                session: this.session!,
              });

              console.error(e);

              this.micConnectStatusReason.set(e.reason);
              this.micConnectStatus.set(ConnectionStatus.error);

              this.isMicOn.set(false);
            });

            socket.on('warning', (e: any) => {
              console.warn(e);
              this.micConnectStatusReason.set(e.reason);
              this.micConnectStatus.set(ConnectionStatus.warning);

              this.isMicOn.set(false);
            });

            socket.on('Metadata', (e: any) => console.log(e));

            socket.on('close', (e: any) => {
              posthog.capture(PostHogEvent.sessionMicClosed, {
                error: e,
                session: this.session!,
              });

              console.log(e);
              this.micConnectStatusReason.set(e.reason);
              this.micConnectStatus.set(ConnectionStatus.closed);

              this.isMicOn.set(false);
            });

            mediaRecorder.addEventListener('dataavailable', (event) => {
              if (event.data.size > 0) {
                if (socket.getReadyState() === SOCKET_STATES.open) {
                  socket.send(event.data);
                }
              }
            });
            mediaRecorder.start(250);
            this.startVolumeCheck();
          });
        });
    } else {
      this.msg.error('No microphone is detected. Please select a microphone.');
    }
  }

  private volumeCheckInterval: any;

  private startVolumeCheck() {
    if (this.volumeCheckInterval) {
      clearInterval(this.volumeCheckInterval);
    }
    this.volumeCheckInterval = setInterval(() => this.checkVolume(), 250);
  }

  private checkVolume() {
    if (!this.isPause()) {
      if (this.dataArray) {
        this.analyser?.getByteFrequencyData(this.dataArray);
        let sum = 0;
        for (let i = 0; i < this.dataArray.length; i++) {
          sum += this.dataArray[i];
        }
        const average = sum / this.dataArray.length;

        if (average < 20) {
          this.isUserSpeaking.set(false);
        } else {
          this.isUserSpeaking.set(true);
        }
      }
    }
  }

  stopAudio() {
    if (this.audioPlayer && this.audioPlayer.nativeElement) {
      this.audioPlayer.nativeElement.pause();
      this.audioPlayer.nativeElement.currentTime = 0;
      this.aiTalkingEndTime = DateTime.utc();
      this.isAiSpeaking.set(false);
    }
  }

  private talkToLlm(performance?: SessionPerformance) {
    this.isAiThinking.set(true);
    const myAnswerList = this.myAnswerList();

    let lastContent = myAnswerList[myAnswerList.length - 1];
    // if (rerun) {
    //   lastContent = myAnswerList[myAnswerList.length - 2];
    // }

    if (lastContent?.length > 2) {
      let previousConversation: string[] = [];

      if (myAnswerList.length > 1) {
        const previouseCount = 5;

        for (let i = Math.max(0, myAnswerList.length - previouseCount); i < myAnswerList.length; i++) {
          const previousMyAnswer = myAnswerList[i];
          const previousAiResponse = this.aiResponseList()[i];

          previousConversation.push(getOneConversationPrompt(previousMyAnswer, previousAiResponse));
        }
      }

      let character: any = undefined;

      if (this.session()?.id) {
        character = {
          firstName: this.session()!.subData.resume?.subData?.content?.firstName,
          lastName: this.session()!.subData.resume?.subData?.content?.lastName,
          resume: convertResumeJsonToMarkdown(this.session()!.subData.resume?.subData?.content as any, false),
        };
      }

      let salesCycle: SalesCycle | undefined = undefined;
      const salesCycleList = this.workspaceHolderService.currentWorkspace().settings?.salesCycleList;

      if (!salesCycleList || !this.session()?.subData.salesCycle) {
        salesCycle = initSalesCycleData[0];
      } else {
        salesCycle = this.session()!.subData.salesCycle;
      }

      if (!salesCycle) {
        salesCycle = initSalesCycleData[0];
      }

      const allObjectionList = this.allObjectionList();

      const emotionalObjectionList = allObjectionList.filter((c) => c.type === ObjectionType.emotional);
      const factObjectionList = allObjectionList.filter((c) => c.type === ObjectionType.fact);

      const prompt = getCustomerPrompt({
        language: this.session()!.subData.language,
        instruction: this.session()!.subData.template?.unsafeSubData?.instruction,
        salesCycle,
        personalityList: this.session()!.subData?.personalityList,
        previousConversationList: previousConversation,
        character,
        additionalPrompt: '',
        product: {
          isFamiliarProduct: this.session()!.subData.isFamiliarProduct ?? false,
          product: this.session()!.subData.product,
        },
        emotionalObjectionList,
        factObjectionList,
      });

      const model =
        performance ?? this.performanceOptions[this.selectedPerformanceIndex ?? 0] === SessionPerformance.Speed
          ? OpenaiModel.gpt35
          : OpenaiModel.gpt4;

      const temperature =
        this.creativityOptions[this.selectedCreativityIndex ?? 0] === SessionCreativity.Deterministic ? 0.2 : 0.8;

      this.openaiService
        .getAnswer$(
          { messageList: [{ role: 'user', content: prompt }], config: { openaiModel: model, temperature } },
          true
        )
        .pipe(take(1))
        .subscribe((result) => {
          this.tempAiText = result;

          this.deepgramService
            .textToSpeech$(this.session()!.id, this.tempAiText, {
              voice: this.session()!.subData.resume?.subData?.content?.voice,
            })
            .pipe(take(1))
            .subscribe((url) => {
              this.currentAudioUrl = url;
              this.audioPlayer.nativeElement.src = url;
              this.aiTalkingStartTime = DateTime.local();
              this.playAudio();
              this.isAiThinking.set(false);
              this.isAiSpeaking.set(true);
            });
        });
    }
  }

  private playAudio() {
    if (this.audioPlayer && this.audioPlayer.nativeElement) {
      const playPromise = this.audioPlayer.nativeElement.play();

      if (playPromise !== undefined) {
        playPromise
          .then((_) => {
            // Playback started successfully
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              this.stopAudio();
              this.msg.warning('Interrupted by a call to pause.');
              console.log('Play request was interrupted, likely due to a pause() call');
            } else {
              this.msg.warning('Error during audio playback.');
              console.error('Error during audio playback:', error);
            }
          });
      }
    }
  }

  //#region Auto Scroll to Bottom
  private _shouldScrollToBottom: boolean = false;

  @ViewChild('chatMessages', { static: false })
  private chatMessagesRef?: ElementRef;

  private scrollAllToBottom(): void {
    this.scrollToBottom(this.chatMessagesRef);
  }

  private scrollToBottom(elementRef?: ElementRef): void {
    const scrollContainer = elementRef?.nativeElement;

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }
  //#endregion
}

export enum ConnectionStatus {
  loading = 'loading',
  connected = 'connected',
  error = 'error',
  closed = 'closed',
  warning = 'warning',
}
